// src/pages/programs/ProgramDetails.js
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useUser } from '@clerk/clerk-react';
import { programsAPI } from '../../services/api';
import toast from 'react-hot-toast';
import QRCode from 'react-qr-code';
import * as htmlToImage from 'html-to-image';
import {
  FaShare,
  FaFacebook,
  FaTwitter,
  FaLinkedin,
  FaWhatsapp,
  FaCopy,
  FaCheckCircle,
  FaSpinner,
  FaGraduationCap,
  FaInfoCircle,
  FaMicrophone,
  FaSpellCheck,
  FaBookOpen,
  FaPalette,
  FaFootballBall,
  FaCrown,
  FaUsers,
  FaQrcode,
  FaDownload,
  FaTimes,
  FaClock,
  FaUserTie,
  FaCalendarWeek,
  FaChartLine,
  FaAward,
  FaStar
} from 'react-icons/fa';

const ProgramDetails = () => {
  const { programId } = useParams();
  const navigate = useNavigate();
  const { isSignedIn, isLoaded } = useUser();
  
  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [enrolling, setEnrolling] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [generatingQR, setGeneratingQR] = useState(false);
  
  // Ref for QR code container
  const qrCodeRef = useRef(null);

  // Icon mapping for programs
  const iconMapping = {
    'FaCrown': FaCrown,
    'FaMicrophone': FaMicrophone,
    'FaUsers': FaUsers,
    'FaSpellCheck': FaSpellCheck,
    'FaBookOpen': FaBookOpen,
    'FaPalette': FaPalette,
    'FaFootballBall': FaFootballBall,
    'FaGraduationCap': FaGraduationCap
  };

  // Color schemes for different program categories
  const getColorClasses = (category) => {
    const schemes = {
      core: 'from-green-500 to-emerald-600',
      academic: 'from-blue-500 to-blue-600',
      cultural: 'from-yellow-500 to-yellow-600',
      creative: 'from-pink-500 to-pink-600',
      premium: 'from-red-500 to-red-600',
      sports: 'from-orange-500 to-orange-600'
    };
    return schemes[category] || 'from-gray-500 to-gray-600';
  };

  const getProgramIcon = (program) => {
    if (program.icon && iconMapping[program.icon]) {
      return iconMapping[program.icon];
    }
    return FaGraduationCap;
  };

  // Get thumbnail URL with fallback
  const getThumbnailUrl = (program) => {
    if (program.thumbnail) {
      if (program.thumbnail.startsWith('http')) {
        return program.thumbnail;
      }
      const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      return `${baseUrl}${program.thumbnail.startsWith('/') ? '' : '/'}${program.thumbnail}`;
    }
    
    // Fallback based on category
    const fallbackImages = {
      'core': 'https://images.unsplash.com/photo-1573164713714-d95e436ab8d6?w=1200&h=800&fit=crop',
      'academic': 'https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=1200&h=800&fit=crop',
      'cultural': 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=1200&h=800&fit=crop',
      'creative': 'https://images.unsplash.com/photo-1513475382585-d06e58bcb0e0?w=1200&h=800&fit=crop',
      'premium': 'https://images.unsplash.com/photo-1577896851231-70ef18881754?w=1200&h=800&fit=crop'
    };
    
    return fallbackImages[program.category] || fallbackImages['core'];
  };

  // Fetch program details
  useEffect(() => {
    const fetchProgramDetails = async () => {
      try {
        setLoading(true);
        const response = await programsAPI.getById(programId);

        if (response.success) {
          setProgram(response.program);

          // 🔍 DEBUG: Check data types and structure
          console.log('========================================');
          console.log('📊 FULL PROGRAM DATA:', response.program);
          console.log('========================================');
          console.log('✅ Program Title:', response.program.title);
          console.log('✅ Program ID:', response.program._id || response.program.id);
          console.log('========================================');
          console.log('📝 Features Type:', typeof response.program.features);
          console.log('📝 Features Data:', response.program.features);
          console.log('📝 Features is Array?', Array.isArray(response.program.features));
          if (Array.isArray(response.program.features)) {
            console.log('📝 First Feature Type:', typeof response.program.features[0]);
            console.log('📝 First Feature Value:', response.program.features[0]);
          }
          console.log('========================================');
          console.log('🎯 Objectives Type:', typeof response.program.objectives);
          console.log('🎯 Objectives Data:', response.program.objectives);
          console.log('🎯 Objectives is Array?', Array.isArray(response.program.objectives));
          if (Array.isArray(response.program.objectives)) {
            console.log('🎯 First Objective Type:', typeof response.program.objectives[0]);
            console.log('🎯 First Objective Value:', response.program.objectives[0]);
          }
          console.log('========================================');
          console.log('📚 Materials Type:', typeof response.program.materials);
          console.log('📚 Materials Data:', response.program.materials);
          console.log('📚 Materials is Array?', Array.isArray(response.program.materials));
          if (Array.isArray(response.program.materials)) {
            console.log('📚 First Material Type:', typeof response.program.materials[0]);
            console.log('📚 First Material Value:', response.program.materials[0]);
          }
          console.log('========================================');
          console.log('📅 Schedule Type:', typeof response.program.schedule);
          console.log('📅 Schedule Data:', response.program.schedule);
          if (response.program.schedule?.days) {
            console.log('📅 Schedule Days Type:', typeof response.program.schedule.days);
            console.log('📅 Schedule Days Data:', response.program.schedule.days);
            console.log('📅 Schedule Days is Array?', Array.isArray(response.program.schedule.days));
            if (Array.isArray(response.program.schedule.days)) {
              console.log('📅 First Day Type:', typeof response.program.schedule.days[0]);
              console.log('📅 First Day Value:', response.program.schedule.days[0]);
            }
          }
          console.log('========================================');
          console.log('👨‍🏫 Instructor Type:', typeof response.program.instructor);
          console.log('👨‍🏫 Instructor Data:', response.program.instructor);
          if (response.program.instructor?.qualifications) {
            console.log('👨‍🏫 Qualifications Type:', typeof response.program.instructor.qualifications);
            console.log('👨‍🏫 Qualifications Data:', response.program.instructor.qualifications);
          }
          console.log('========================================');

          // Check if user is enrolled for this program
          if (isSignedIn) {
            const userProgramsResponse = await programsAPI.getUserPrograms();
            const userPrograms = userProgramsResponse.programs || [];
            const isUserEnrolled = userPrograms.some(up => up._id === programId || up.programId === programId);
            setIsEnrolled(isUserEnrolled);
          }
        } else {
          throw new Error('Program not found');
        }
      } catch (error) {
        console.error('❌ Error fetching program:', error);
        toast.error('Failed to load program details');
        navigate('/programs');
      } finally {
        setLoading(false);
      }
    };

    if (programId) {
      fetchProgramDetails();
    }
  }, [programId, isSignedIn, isLoaded, navigate]);

  // Handle program enrollment
  const handleEnroll = async () => {
    if (!isSignedIn) {
      toast.error('Please login to enroll in programs');
      navigate('/login');
      return;
    }

    if (isEnrolled) {
      toast.info('You are already enrolled in this program');
      return;
    }

    try {
      setEnrolling(true);
      const response = await programsAPI.enroll(programId);
      
      if (response.success) {
        setIsEnrolled(true);
        toast.success('Successfully enrolled in program!');
      } else {
        throw new Error(response.message || 'Enrollment failed');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to enroll in program');
    } finally {
      setEnrolling(false);
    }
  };

  // Generate and download QR code
  const downloadQRCode = async () => {
    if (!qrCodeRef.current) return;

    try {
      setGeneratingQR(true);
      
      const dataUrl = await htmlToImage.toPng(qrCodeRef.current, {
        backgroundColor: 'white',
        width: 300,
        height: 350,
        style: {
          padding: '20px',
        }
      });

      const link = document.createElement('a');
      link.download = `${program.title.replace(/[^a-zA-Z0-9]/g, '-')}-QRCode.png`;
      link.href = dataUrl;
      link.click();

      toast.success('QR code downloaded successfully!');
    } catch (error) {
      console.error('Error generating QR code:', error);
      toast.error('Failed to download QR code');
    } finally {
      setGeneratingQR(false);
    }
  };

  // Share functionality
  const currentUrl = window.location.href;

  const shareOnSocial = (platform) => {
    const text = `Check out this program: ${program.title}`;
    const urls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(currentUrl)}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(currentUrl)}&text=${encodeURIComponent(text)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(currentUrl)}`,
      whatsapp: `https://wa.me/?text=${encodeURIComponent(text + ' ' + currentUrl)}`
    };

    if (urls[platform]) {
      window.open(urls[platform], '_blank', 'width=600,height=400');
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(currentUrl);
    setCopySuccess(true);
    toast.success('Link copied to clipboard!');
    setTimeout(() => setCopySuccess(false), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20 flex items-center justify-center">
        <FaSpinner className="w-8 h-8 text-green-500 animate-spin" />
      </div>
    );
  }

  if (!program) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">Program not found</p>
          <Link to="/programs" className="text-green-500 hover:underline">
            Back to Programs
          </Link>
        </div>
      </div>
    );
  }

  const ProgramIcon = getProgramIcon(program);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white pt-20">
      {/* Hero Section with Gradient Background */}
      <section className="relative py-16 px-4 sm:px-6 lg:px-8 overflow-hidden">
        {/* Gradient Background */}
        <div className={`absolute inset-0 bg-gradient-to-r ${getColorClasses(program.category)} opacity-10`}></div>
        
        <div className="container mx-auto max-w-6xl relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            {/* Left: Program Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              {/* Badge */}
              {program.badge && (
                <div className="inline-block px-4 py-2 bg-green-100 rounded-full border border-green-200 mb-4">
                  <span className="text-green-700 text-sm font-medium">{program.badge}</span>
                </div>
              )}

              {/* Featured Badge */}
              {program.isFeatured && (
                <div className="inline-block px-3 py-1 bg-yellow-100 rounded-full border border-yellow-300 mb-4 ml-2">
                  <div className="flex items-center gap-1">
                    <FaStar className="w-3 h-3 text-yellow-600" />
                    <span className="text-yellow-700 text-xs font-medium">Featured</span>
                  </div>
                </div>
              )}

              {/* Title */}
              <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4 leading-tight">
                {program.title}
              </h1>

              {/* Category & Icon */}
              <div className="flex items-center gap-3 mb-6">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-r ${getColorClasses(program.category)} flex items-center justify-center shadow-lg`}>
                  <ProgramIcon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">{program.category}</p>
                  <p className="text-sm text-gray-600">
                    {program.targetAge && `Ages ${program.targetAge.min}-${program.targetAge.max}`}
                  </p>
                </div>
              </div>

              {/* Enrollment Status */}
              <div className="mb-6">
                <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                  program.enrollmentStatus === 'open' 
                    ? 'bg-green-100 text-green-700 border border-green-200'
                    : program.enrollmentStatus === 'waitlist'
                    ? 'bg-yellow-100 text-yellow-700 border border-yellow-200'
                    : 'bg-red-100 text-red-700 border border-red-200'
                }`}>
                  {program.enrollmentStatus === 'open' ? '✓ Open for Enrollment' 
                    : program.enrollmentStatus === 'waitlist' ? 'Waitlist Only' 
                    : 'Enrollment Closed'}
                </span>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={handleEnroll}
                  disabled={enrolling || isEnrolled || program.enrollmentStatus === 'closed'}
                  className={`px-8 py-3 rounded-full font-semibold transition-all duration-300 shadow-lg ${
                    isEnrolled
                      ? 'bg-green-100 text-green-700 cursor-default'
                      : program.enrollmentStatus === 'closed'
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:shadow-xl hover:scale-105'
                  }`}
                >
                  {enrolling ? (
                    <><FaSpinner className="inline mr-2 animate-spin" />Enrolling...</>
                  ) : isEnrolled ? (
                    <><FaCheckCircle className="inline mr-2" />Enrolled</>
                  ) : (
                    'Enroll Now'
                  )}
                </button>

                {/* Share Button */}
                <div className="relative">
                  <button
                    onClick={() => setShowShareMenu(!showShareMenu)}
                    className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-full font-semibold hover:border-green-500 hover:text-green-600 transition-all duration-300"
                  >
                    <FaShare className="inline mr-2" />
                    Share
                  </button>

                  {showShareMenu && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="absolute top-full left-0 mt-2 bg-white rounded-xl shadow-2xl p-4 z-20 border border-gray-100"
                    >
                      <div className="flex gap-2 mb-3">
                        <button onClick={() => shareOnSocial('facebook')} className="p-2 hover:bg-blue-50 rounded-lg transition-colors">
                          <FaFacebook className="w-5 h-5 text-blue-600" />
                        </button>
                        <button onClick={() => shareOnSocial('twitter')} className="p-2 hover:bg-sky-50 rounded-lg transition-colors">
                          <FaTwitter className="w-5 h-5 text-sky-500" />
                        </button>
                        <button onClick={() => shareOnSocial('linkedin')} className="p-2 hover:bg-blue-50 rounded-lg transition-colors">
                          <FaLinkedin className="w-5 h-5 text-blue-700" />
                        </button>
                        <button onClick={() => shareOnSocial('whatsapp')} className="p-2 hover:bg-green-50 rounded-lg transition-colors">
                          <FaWhatsapp className="w-5 h-5 text-green-600" />
                        </button>
                      </div>
                      <div className="flex gap-2 pt-2 border-t border-gray-100">
                        <button onClick={copyLink} className="flex-1 px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium flex items-center justify-center gap-2">
                          {copySuccess ? <FaCheckCircle className="text-green-500" /> : <FaCopy />}
                          {copySuccess ? 'Copied!' : 'Copy Link'}
                        </button>
                        <button onClick={() => setShowQRModal(true)} className="flex-1 px-3 py-2 bg-green-100 rounded-lg hover:bg-green-200 transition-colors text-sm font-medium flex items-center justify-center gap-2 text-green-700">
                          <FaQrcode />
                          QR Code
                        </button>
                      </div>
                    </motion.div>
                  )}
                </div>
              </div>
            </motion.div>

            {/* Right: Program Thumbnail */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src={getThumbnailUrl(program)}
                  alt={program.title}
                  className="w-full h-80 object-cover"
                />
                <div className={`absolute inset-0 bg-gradient-to-t ${getColorClasses(program.category)} opacity-20`}></div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* QR Code Modal */}
      {showQRModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white rounded-2xl p-6 max-w-sm w-full mx-auto"
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Share Program</h3>
              <button
                onClick={() => setShowQRModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <FaTimes className="w-4 h-4" />
              </button>
            </div>

            <div ref={qrCodeRef} className="text-center mb-6">
              <div className="bg-white p-4 rounded-lg inline-block">
                <QRCode
                  size={200}
                  style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                  value={currentUrl}
                  viewBox="0 0 256 256"
                  level="M"
                />
              </div>
              <div className="mt-3">
                <p className="font-semibold text-gray-900 text-sm">{program.title}</p>
                <p className="text-gray-600 text-xs mt-1">
                  {program.category} • {program.duration || 'Flexible Duration'}
                </p>
              </div>
            </div>

            <button
              onClick={downloadQRCode}
              disabled={generatingQR}
              className={`w-full py-3 px-4 rounded-lg font-semibold transition-all duration-300 flex items-center justify-center ${
                generatingQR
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-green-500 text-white hover:bg-green-600'
              }`}
            >
              {generatingQR ? (
                <>
                  <FaSpinner className="w-4 h-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <FaDownload className="w-4 h-4 mr-2" />
                  Download QR Code
                </>
              )}
            </button>

            <p className="text-xs text-gray-500 text-center mt-3">
              Scan this QR code to quickly access the program page
            </p>
          </motion.div>
        </div>
      )}

      {/* Program Details Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* About Section */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100"
              >
                <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                  <FaInfoCircle className="text-green-500" />
                  About This Program
                </h2>
                
                <p className="text-gray-700 leading-relaxed text-base mb-6">
                  {program.description || 'This comprehensive program is designed to develop essential skills and knowledge in a supportive learning environment.'}
                </p>

                {/* Key Features */}
                {program.features && program.features.length > 0 && program.features[0] !== '' && (
                  <div className="mb-8">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <FaChartLine className="text-green-500" />
                      Key Features
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {program.features.map((feature, index) => (
                        <div key={index} className="flex items-start gap-3 p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-100 hover:shadow-md transition-shadow">
                          <div className="w-2 h-2 bg-green-500 rounded-full flex-shrink-0 mt-2"></div>
                          <span className="text-gray-700 text-sm font-medium">
                            {typeof feature === 'object' ? JSON.stringify(feature) : String(feature)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Objectives */}
                {program.objectives && program.objectives.length > 0 && program.objectives[0] !== '' && (
                  <div className="mb-8">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <FaAward className="text-blue-500" />
                      Learning Objectives
                    </h3>
                    <div className="space-y-3">
                      {program.objectives.map((objective, index) => (
                        <div key={index} className="flex items-start gap-3 p-4 bg-blue-50 rounded-xl border border-blue-100">
                          <div className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">
                            {index + 1}
                          </div>
                          <span className="text-gray-700 text-sm">
                            {typeof objective === 'object' ? JSON.stringify(objective) : String(objective)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Schedule */}
                {program.schedule && (
                  <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-100">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <FaCalendarWeek className="text-purple-500" />
                      Schedule
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Frequency</p>
                        <p className="font-semibold text-gray-900">{program.schedule.frequency || 'Weekly'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Session Duration</p>
                        <p className="font-semibold text-gray-900">{program.schedule.duration || '2 hours'}</p>
                      </div>
                      {program.schedule.days && program.schedule.days.length > 0 && (
                        <div className="col-span-2">
                          <p className="text-sm text-gray-600 mb-2">Days</p>
                          <div className="flex flex-wrap gap-2">
                            {program.schedule.days.map((day, idx) => (
                              <span key={idx} className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
                                {typeof day === 'object' ? JSON.stringify(day) : String(day)}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </motion.div>

              {/* Instructor Section */}
              {program.instructor && program.instructor.name && (
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.3 }}
                  className="bg-gradient-to-r from-orange-50 to-yellow-50 rounded-2xl p-8 shadow-lg border border-orange-100"
                >
                  <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                    <FaUserTie className="text-orange-500" />
                    Your Instructor
                  </h2>
                  
                  <div className="space-y-4">
                    <div>
                      <p className="text-lg font-bold text-gray-900">{program.instructor.name}</p>
                    </div>
                    
                    {program.instructor.bio && (
                      <p className="text-gray-700 leading-relaxed">{program.instructor.bio}</p>
                    )}
                    
                    {program.instructor.qualifications && program.instructor.qualifications.length > 0 && (
                      <div>
                        <p className="text-sm font-semibold text-gray-700 mb-2">Qualifications:</p>
                        <div className="flex flex-wrap gap-2">
                          {program.instructor.qualifications.map((qual, idx) => (
                            <span key={idx} className="px-3 py-1 bg-white rounded-full text-sm text-gray-700 border border-orange-200">
                              {typeof qual === 'object' ? JSON.stringify(qual) : String(qual)}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}

              {/* Materials */}
              {program.materials && program.materials.length > 0 && program.materials[0] !== '' && (
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.4 }}
                  className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100"
                >
                  <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                    <FaBookOpen className="text-indigo-500" />
                    Materials Provided
                  </h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {program.materials.map((material, index) => (
                      <div key={index} className="flex items-center gap-3 p-3 bg-indigo-50 rounded-lg border border-indigo-100">
                        <div className="w-2 h-2 bg-indigo-500 rounded-full"></div>
                        <span className="text-gray-700 text-sm">
                          {typeof material === 'object' ? JSON.stringify(material) : String(material)}
                        </span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="space-y-6 sticky top-24"
              >
                {/* Program Stats */}
                <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Program Details</h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center pb-3 border-b border-gray-100">
                      <span className="text-gray-600 text-sm flex items-center gap-2">
                        <FaClock className="text-gray-400" />
                        Duration
                      </span>
                      <span className="font-semibold text-gray-900">{program.duration || 'Flexible'}</span>
                    </div>
                    
                    <div className="flex justify-between items-center pb-3 border-b border-gray-100">
                      <span className="text-gray-600 text-sm flex items-center gap-2">
                        <FaUsers className="text-gray-400" />
                        Capacity
                      </span>
                      <span className="font-semibold text-gray-900">{program.capacity || 30} students</span>
                    </div>
                    
                    <div className="flex justify-between items-center pb-3 border-b border-gray-100">
                      <span className="text-gray-600 text-sm flex items-center gap-2">
                        <FaGraduationCap className="text-gray-400" />
                        Age Range
                      </span>
                      <span className="font-semibold text-gray-900">
                        {program.targetAge ? `${program.targetAge.min}-${program.targetAge.max} years` : 'All Ages'}
                      </span>
                    </div>
                    
                    {program.price && program.price.amount > 0 && (
                      <div className="flex justify-between items-center pb-3 border-b border-gray-100">
                        <span className="text-gray-600 text-sm">Price</span>
                        <span className="font-bold text-green-600 text-lg">
                          {program.price.currency} {program.price.amount.toLocaleString()}
                        </span>
                      </div>
                    )}
                    
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 text-sm flex items-center gap-2">
                        <FaStar className="text-yellow-400" />
                        Rating
                      </span>
                      <span className="font-semibold text-gray-900">{program.rating || '5.0'}/5</span>
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6 shadow-lg border border-green-100">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Need Help?</h3>
                  <div className="space-y-3">
                    <div>
                      <p className="text-gray-600 text-xs mb-1">Program Coordinator</p>
                      <p className="font-semibold text-gray-900">{program.coordinator || 'ACD Team'}</p>
                    </div>
                    <div>
                      <p className="text-gray-600 text-xs mb-1">Contact Email</p>
                      <a href={`mailto:${program.contactEmail || 'info@africandebatechampions.org'}`} className="font-medium text-green-600 hover:text-green-700 text-sm">
                        {program.contactEmail || 'info@africandebatechampions.org'}
                      </a>
                    </div>
                    <div>
                      <p className="text-gray-600 text-xs mb-1">Phone</p>
                      <a href={`tel:${program.contactPhone || '+254718290692'}`} className="font-medium text-green-600 hover:text-green-700 text-sm">
                        {program.contactPhone || '+254 718 290 692'}
                      </a>
                    </div>
                  </div>
                </div>

                {/* Related Programs */}
                <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Explore More</h3>
                  <div className="space-y-3">
                    <Link to="/programs" className="block p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-all hover:shadow-md">
                      <p className="font-semibold text-gray-900 text-sm mb-1">Browse All Programs</p>
                      <p className="text-xs text-gray-600">Discover more training opportunities</p>
                    </Link>
                    <Link to="/events" className="block p-4 bg-green-50 rounded-xl hover:bg-green-100 transition-all hover:shadow-md">
                      <p className="font-semibold text-green-900 text-sm mb-1">View Events</p>
                      <p className="text-xs text-green-600">Join upcoming competitions</p>
                    </Link>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProgramDetails;
